import { useEffect, useRef, type ReactNode } from 'react';
import { T, Var, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { TrashIcon } from '@phosphor-icons/react';
import { nextRouteIdentity } from './partyRouteSlug';
import type {
  IdentifyPartyEntityName,
  IdentifyPartyPicker,
} from './identifyPartyFormConfig';
import type { PartyRoute } from './parsePartyRoutes';

export function IdentifyPartyRouteCard({
  route,
  index,
  total,
  siblingIds,
  reserved,
  slugFallback,
  duplicate,
  duplicateMessage,
  noEntitySelectedLabel,
  renderPicker,
  EntityName,
  onChange,
  onRemove,
}: {
  route: PartyRoute;
  index: number;
  total: number;
  siblingIds: Set<string>;
  reserved: Set<string>;
  slugFallback: string;
  duplicate: boolean;
  duplicateMessage: ReactNode;
  noEntitySelectedLabel: ReactNode;
  renderPicker: IdentifyPartyPicker;
  EntityName: IdentifyPartyEntityName;
  onChange: (updated: PartyRoute) => void;
  onRemove: () => void;
}) {
  const id = route.entityId.length > 0 ? route.entityId : undefined;
  return (
    <EntityName id={id}>
      {(entityName) => (
        <IdentifyPartyNamedRouteCard
          route={route}
          index={index}
          total={total}
          siblingIds={siblingIds}
          reserved={reserved}
          slugFallback={slugFallback}
          duplicate={duplicate}
          duplicateMessage={duplicateMessage}
          noEntitySelectedLabel={noEntitySelectedLabel}
          renderPicker={renderPicker}
          entityName={entityName}
          onChange={onChange}
          onRemove={onRemove}
        />
      )}
    </EntityName>
  );
}

function IdentifyPartyNamedRouteCard({
  route,
  index,
  total,
  siblingIds,
  reserved,
  slugFallback,
  duplicate,
  duplicateMessage,
  noEntitySelectedLabel,
  renderPicker,
  entityName,
  onChange,
  onRemove,
}: {
  route: PartyRoute;
  index: number;
  total: number;
  siblingIds: Set<string>;
  reserved: Set<string>;
  slugFallback: string;
  duplicate: boolean;
  duplicateMessage: ReactNode;
  noEntitySelectedLabel: ReactNode;
  renderPicker: IdentifyPartyPicker;
  entityName: string | undefined;
  onChange: (updated: PartyRoute) => void;
  onRemove: () => void;
}) {
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    if (!entityName) return;
    const next = nextRouteIdentity({
      currentId: route.id,
      currentLabel: route.label,
      entityName,
      siblingIds,
      reserved,
      slugFallback,
    });
    if (!next) return;
    onChangeRef.current({ ...route, id: next.id, label: next.label });
  }, [entityName, reserved, route, siblingIds, slugFallback]);

  return (
    <div className="rounded-md border p-3 space-y-3">
      <IdentifyPartyRouteCardHeading
        index={index}
        total={total}
        entityName={entityName}
        routeId={route.id}
        noEntitySelectedLabel={noEntitySelectedLabel}
        onRemove={onRemove}
      />

      {renderPicker(
        route.entityId.length > 0 ? route.entityId : undefined,
        (next) => onChange({ ...route, entityId: next ?? '' }),
      )}

      {duplicate && (
        <p className="text-[10px] text-destructive">{duplicateMessage}</p>
      )}
    </div>
  );
}

function IdentifyPartyRouteCardHeading({
  index,
  total,
  entityName,
  routeId,
  noEntitySelectedLabel,
  onRemove,
}: {
  index: number;
  total: number;
  entityName: string | undefined;
  routeId: string;
  noEntitySelectedLabel: ReactNode;
  onRemove: () => void;
}) {
  const gt = useGT();
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-muted-foreground">
          <T>
            Route <Var>{index + 1}</Var> of <Var>{total}</Var>
          </T>
        </div>
        {entityName ? (
          <div className="text-sm font-medium truncate">{entityName}</div>
        ) : (
          <div className="text-sm italic text-muted-foreground">
            {noEntitySelectedLabel}
          </div>
        )}
        {routeId && entityName && (
          <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
            <T>
              socket: <Var>{routeId}</Var>
            </T>
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 text-destructive hover:text-destructive shrink-0"
        onClick={onRemove}
        aria-label={gt('Remove route')}
      >
        <TrashIcon className="w-3 h-3" />
      </Button>
    </div>
  );
}
